<template>
  <div>
    <InputField
      id="storageName"
      v-model.trim="name"
      label="Name"
      :error="error.name"
      class="storage__field"
      label-info-tooltip="New name for the setting."
    />

    <InputField
      id="storageBucket"
      v-model.trim="bucket"
      label="Bucket"
      :error="error.bucket"
      class="storage__field"
      label-info-tooltip="Name of the storage bucket / storage account name."
    />

    <InputField
      v-model.trim="prefix"
      label="Prefix"
      :error="error.prefix"
      class="storage__field"
      label-info-tooltip=" Prefix for all Darwin-created files (like thumbnails).
        It cannot start nor end with '/'. Examples: 'v7labs', 'mycontainer/darwin'."
    />

    <InputField
      id="baseUrl"
      v-model.trim="baseUrl"
      label="Base Url"
      :error="error.baseUrl"
      class="storage__field"
      label-info-tooltip="Base URL for MinIO server."
    />

    <RadioGroup
      class="storage__field"
      :options="readOnlyOptions"
      :value="isReadonly"
      size="md"
      @change="isReadonly = $event"
    />

    <InputField
      v-if="isReadonlyStorage"
      v-model.trim="filepath"
      label="Validation File Path"
      :error="error.filepath"
      class="storage__field"
      placeholder="v7_test.txt"
      label-info-tooltip="Optional file path for read-only validation. If not specified, we'll look for 'v7_test.txt' in the bucket root to verify access. This file should exist in your storage bucket."
    />

    <Divider class="divider" />

    <div class="control__toggle">
      <Toggle
        v-model="isDefault"
        kind="primary"
        size="small"
      />

      <div class="control__toggle-description">
        <p class="control__toggle-label">Set as default</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'MinioStorageForm',
}
</script>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import isEmpty from 'validator/lib/isEmpty'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import type { StoragePayload } from '@/store/types'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import RadioGroup from '@/uiKit/RadioGroup/RadioGroup.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import { readOnlyOptions } from './data'

type MinioStorageFormError = {
  name?: string
  bucket?: string
  prefix?: string
  baseUrl?: string
  filepath?: string
}

const props = withDefaults(
  defineProps<{
    value?: StoragePayload | null
  }>(),
  { value: null },
)

const emit = defineEmits<{
  (e: 'submitted', payload: StoragePayload): void
}>()

const isReadonly = ref('0')
const name = ref('')
const bucket = ref('')
const prefix = ref('')
const baseUrl = ref('')
const filepath = ref('')
const isDefault = ref(false)
const error = ref<MinioStorageFormError>({})

const isReadonlyStorage = computed(() => isReadonly.value === '1')

const validateForm = (): boolean => {
  error.value = {}
  if (isEmpty(name.value.trim())) {
    error.value.name = 'Storage name cannot be empty'
  }

  if (isEmpty(bucket.value.trim())) {
    error.value.bucket = 'Bucket cannot be empty'
  }

  const trimPrefix = prefix.value.trim()
  if (
    trimPrefix.length &&
    (trimPrefix.charAt(0) === '/' || trimPrefix.charAt(trimPrefix.length - 1) === '/')
  ) {
    error.value.prefix = "Prefix cannot start or end with '/'"
  }

  if (isEmpty(baseUrl.value.trim())) {
    error.value.baseUrl = 'Base url cannot be empty'
  }

  return Object.keys(error.value).length === 0
}

const onSubmit = (): void => {
  if (!validateForm()) {
    return
  }

  emit('submitted', {
    slug: props.value?.slug || '',
    name: name.value.trim(),
    readonly: isReadonly.value === '1',
    bucket: bucket.value.trim(),
    prefix: prefix.value.trim(),
    base_url: baseUrl.value.trim(),
    filepath: filepath.value.trim() || undefined,
    default: isDefault.value,
  })
}

onMounted((): void => {
  if (props.value) {
    name.value = props.value.name
    bucket.value = props.value.bucket
    prefix.value = props.value.prefix || ''
    baseUrl.value = props.value.base_url || ''
    filepath.value = props.value.filepath || ''
    isDefault.value = props.value.default
    isReadonly.value = props.value.readonly ? '1' : '0'
  }
})

defineExpose({ onSubmit })
</script>

<style lang="scss" scoped>
@import './FormBase.scss';
</style>
